import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  IonButton,
  IonContent,
  IonIcon,
  IonSkeletonText,
  IonText,
} from '@ionic/angular/standalone';
import { WorkspaceSummary } from '@adminpages/domain-models';
import { PosStoreService } from '@adminpages/pos-state';

@Component({
  selector: 'foco-pos-workspace-page',
  standalone: true,
  imports: [IonButton, IonContent, IonIcon, IonSkeletonText, IonText],
  template: `
    <ion-content class="pos-page workspace-content">
      <main class="workspace-shell">
        <header>
          <p>Selecciona workspace</p>
          <h1>¿Dónde vas a operar?</h1>
          <ion-text color="medium">La app habilita POS, Taller y Caja según la configuración del workspace.</ion-text>
        </header>

        <section class="workspace-grid">
          @if (store.workspacesLoading()) {
            @for (item of [1, 2]; track item) {
              <article class="workspace-card skeleton-card">
                <span class="workspace-icon">
                  <ion-skeleton-text animated></ion-skeleton-text>
                </span>
                <ion-skeleton-text animated style="width: 70%; height: 24px"></ion-skeleton-text>
                <ion-skeleton-text animated style="width: 46%"></ion-skeleton-text>
              </article>
            }
          } @else {
            @for (workspace of store.workspaces(); track workspace.id) {
              <button
                type="button"
                class="workspace-card"
                [class.active]="workspace.id === store.selectedWorkspace().id"
                [disabled]="store.catalogLoading() || store.cashRegistersLoading()"
                (click)="select(workspace)"
              >
                <span class="workspace-icon">
                  <ion-icon name="storefront-outline"></ion-icon>
                </span>
                <strong>{{ workspace.name }}</strong>
                <small>{{ workspace.slug }}</small>
              </button>
            }
          }
        </section>

        @if (store.errorMessage(); as error) {
          <ion-text color="danger">{{ error }}</ion-text>
        }

        <ion-button size="large" [disabled]="store.catalogLoading() || store.cashRegistersLoading()" (click)="goSale()">
          {{ store.catalogLoading() || store.cashRegistersLoading() ? 'Cargando...' : entryLabel() }}
        </ion-button>
      </main>
    </ion-content>
  `,
  styles: `
    .workspace-content {
      --padding-bottom: calc(24px + var(--ion-safe-area-bottom, 0px));
      --padding-end: calc(24px + var(--ion-safe-area-right, 0px));
      --padding-start: calc(24px + var(--ion-safe-area-left, 0px));
      --padding-top: calc(24px + var(--ion-safe-area-top, 0px));
    }

    .workspace-shell {
      display: grid;
      gap: 24px;
      margin: 0 auto;
      max-width: 920px;
      min-height: calc(100vh - 48px);
      min-height: calc(
        100dvh - 48px - var(--ion-safe-area-top, 0px) - var(--ion-safe-area-bottom, 0px)
      );
      place-content: center;
    }

    header p {
      color: var(--pos-color-primary);
      font-weight: 800;
      margin: 0 0 8px;
      text-transform: uppercase;
    }

    header h1 {
      font-size: clamp(34px, 6vw, 60px);
      margin: 0 0 8px;
    }

    .workspace-grid {
      display: grid;
      gap: 14px;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .workspace-card {
      background: var(--pos-color-surface);
      border: 2px solid transparent;
      border-radius: 8px;
      box-shadow: 0 10px 28px var(--pos-color-shadow);
      color: var(--pos-color-text);
      cursor: pointer;
      display: grid;
      gap: 8px;
      min-height: 170px;
      padding: 20px;
      text-align: left;
    }

    .skeleton-card {
      cursor: default;
    }

    .skeleton-card .workspace-icon ion-skeleton-text {
      height: 100%;
      margin: 0;
      width: 100%;
    }

    .workspace-card.active {
      border-color: var(--pos-color-primary);
    }

    .workspace-card:disabled {
      cursor: wait;
      opacity: 0.68;
    }

    .workspace-icon {
      align-items: center;
      background: var(--pos-color-input);
      border-radius: 8px;
      color: var(--pos-color-primary);
      display: inline-flex;
      font-size: 30px;
      height: 56px;
      justify-content: center;
      width: 56px;
    }

    .workspace-card strong {
      font-size: 22px;
    }

    .workspace-card small {
      color: var(--pos-color-muted);
    }

    @media (max-width: 680px) {
      .workspace-grid {
        grid-template-columns: 1fr;
      }
    }
  `,
})
export class WorkspacePageComponent {
  private readonly router = inject(Router);
  readonly store = inject(PosStoreService);

  async select(workspace: WorkspaceSummary): Promise<void> {
    if (this.store.catalogLoading()) {
      return;
    }

    await this.store.selectWorkspace(workspace);
  }

  goSale(): void {
    void this.router.navigateByUrl(this.store.entryRoute());
  }

  entryLabel(): string {
    if (!this.store.cashRegisterReady()) {
      return 'Seleccionar caja';
    }

    return this.store.selectedWorkspace().homeRoute === '/repairs'
      ? 'Entrar a Taller'
      : this.store.selectedWorkspace().homeRoute === '/cash-session'
        ? 'Entrar a Caja'
        : 'Entrar a POS';
  }
}
